import { DrawCabinetFrontElement } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawCabinetFrontElement";
import { DrawConstant } from "$src/CGS/domain/dto/constant/DrawConstant";
import { EOrientation } from "$src/CGS/domain/dto/core/EOrientation";
import { LengthMaths } from "$src/CGS/domain/dto/core/Length/LengthMaths";
import LengthValue from "$src/CGS/domain/dto/core/Length/LengthValue";
import { LengthValueMillimeter } from "$src/CGS/domain/dto/core/Length/LengthValueMillimeter";
import { FrontElementComputation } from "./FrontElementComputation";
import { InsideComputation } from "./InsideComputation";

export class FrontElementGroupComputation extends FrontElementComputation {
    public orientation: EOrientation;
    public elements: FrontElementComputation[];

    constructor(constants: DrawConstant, context: DrawCabinetFrontElement, 
        orientation: EOrientation, 
        elements: FrontElementComputation[],
        belowContext?: DrawCabinetFrontElement, aboveContext?: DrawCabinetFrontElement, 
        inside?: InsideComputation, hasElementAbove: boolean = false, hasElementBelow: boolean = false,
        isWithGap: boolean = false) 
    {
        super(constants, context, belowContext, aboveContext, inside, hasElementAbove, hasElementBelow, isWithGap);
        this.orientation = orientation;
        this.elements = elements;
    }

    get minWidth(): number {
        return this.orientation === EOrientation.Horizontal
            ? this.elements.reduce((sum, e) => sum + e.minWidth, 0) + this.getTotalGapSize().millimeter
            : Math.max(...this.elements.map(e => e.minWidth));
    }

    get minHeight(): number {
        return this.orientation === EOrientation.Horizontal
            ? Math.max(...this.elements.map(e => e.minHeight))
            : this.elements.reduce((sum, e) => sum + e.minHeight, 0) + this.getTotalGapSize().millimeter;
    }

    get maxWidth(): number | null {
        if (this.elements.some(e => e.maxWidth === null)) {
            return null;
        }

        return this.orientation === EOrientation.Horizontal
            ? this.elements.reduce((sum, e) => sum + (e.maxWidth || 0), 0) + this.getTotalGapSize().millimeter
            : Math.max(...this.elements.map(e => e.maxWidth || 0));
    }

    get maxHeight(): number | null {
        if (this.elements.some(e => e.maxHeight === null)) {
            return null;
        }

        return this.orientation === EOrientation.Horizontal
            ? Math.max(...this.elements.map(e => e.maxHeight || 0))
            : this.elements.reduce((sum, e) => sum + (e.maxHeight || 0), 0) + this.getTotalGapSize().millimeter;
    }

    get size(): number {
        return this.getMin(this.orientation);
    }

    getGapBetweenElement(constant: DrawConstant): LengthValue {
        if (this.orientation === EOrientation.Horizontal) {
            return LengthMaths.multiply(constant.SideBoxGap, 2);
        } else {
            return constant.GapBetweenFrontElements;
        }
    }

    getTotalGapSize(): LengthValue {
        if (this.elements.length === 0) {
            return LengthMaths.Zero;
        }

        if (this.orientation === EOrientation.Horizontal) {
            const gaps = this.elements.reduce((sum, e) => sum + e.getTotalGapSize(this.orientation).millimeter, 0);
            return new LengthValueMillimeter(gaps - (this.constants.SideBoxGap.millimeter * 2));
        } else {
            const gaps = this.elements.reduce((sum, e) => sum + e.getTotalGapSize(this.orientation).millimeter, 0);
            return new LengthValueMillimeter(gaps - this.constants.GapBottomBox.millimeter - this.constants.GapTopBox.millimeter);
        }
    }
}
